import logo from '@/assets/logo.svg'
import { Button } from '@/components/ui/Button'
import { Info } from '@/components/ui/Info'
import { Input } from '@/components/ui/Input'
import { env } from '@/config/env'
import { useAuthStore } from '@/store/auth'
import { useRouteStore } from '@/store/route'
import { ApiError, userService } from '@services'
import { UserRoles } from '@types'
import { useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import styles from './Login.module.scss'

interface LoginFormData {
  email: string
  password: string
}

export const LoginPage = () => {
  const methods = useForm<LoginFormData>()
  const authStore = useAuthStore()
  const routeStore = useRouteStore()
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const onSubmit = async (data: LoginFormData) => {
    setError(null)
    setIsSubmitting(true)
    try {
      const response = await userService.signIn({
        email: data.email,
        password: data.password,
      })

      if (response?.authorization?.auth && response?.authorization?.refresh) {
        const hasDriverRole =
          response.authorization.roles?.includes(UserRoles.CHARITY_DRIVER) ||
          response.authorization.roles?.includes(UserRoles.CAPTAIN)

        if (!hasDriverRole) {
          setError('You do not have permission to access this application')
          return
        }

        authStore.setAuth(
          response,
          response.authorization.auth,
          response.authorization.refresh
        )

        if (response.charity_id) {
          await routeStore.fetchCharity(response.charity_id)
        }

        navigate('/', { replace: true })
      }
    } catch (err) {
      if (err instanceof ApiError && err.data?.message) {
        setError(err.data.message)
      } else {
        setError('Invalid email or password')
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className={styles.container} data-testid="login-page">
      <div className={styles.content}>
        <div className={styles.header}>
          <h1 className={styles.title}>Welcome back!</h1>
          <div className={styles.logoWrapper}>
            <img
              src={logo}
              alt="ReSupply"
              className={styles.logo}
              data-testid="login-logo"
            />
            <p className={styles.logoText}>DRIVER</p>
          </div>
        </div>

        <h2 className={styles.subtitle}>Login to your account</h2>
        <p className={styles.description}>
          Enter your email below to login to your account
        </p>

        <FormProvider {...methods}>
          <form
            onSubmit={methods.handleSubmit(onSubmit)}
            className={styles.form}
            data-testid="login-form"
          >
            <Input<LoginFormData>
              name="email"
              type="email"
              label="Email"
              placeholder="m@example.com"
              required
              testId="login-email-input"
              validation={{
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: 'Invalid email address',
                },
              }}
            />

            <div className={styles.passwordWrapper}>
              <a
                href={env.resetPassUrl}
                className={styles.forgotPassword}
                data-testid="login-forgot-password"
              >
                Forgot your password?
              </a>
              <Input<LoginFormData>
                name="password"
                type="password"
                label="Password"
                required
                testId="login-password-input"
                validation={{
                  minLength: {
                    value: 6,
                    message: 'Password must be at least 6 characters',
                  },
                }}
              />
            </div>

            {error && (
              <Info variant="error" testId="login-error">
                {error}
              </Info>
            )}

            <Button
              text="Login"
              variant="primary"
              type="submit"
              loading={isSubmitting}
              testId="login-submit-button"
            />
          </form>
        </FormProvider>
      </div>
    </div>
  )
}
